/**
 * Блоки 1, 2 и 4 «Калькуляции (форма)»: исходные данные, показатели и вывод,
 * чувствительность. Одни и те же — на вкладке «Безубыточность» и на печатной
 * форме.
 */
import styled from 'styled-components';
import type { PrcBreakEven } from '@shared/api';
import { FleetChip, Matrix, ScrollRegion, fleetLeading, fleetType, typo } from '@entities/fleet';
import {
  BREAK_EVEN_LEVEL_LABEL,
  articleLabel,
  breakEvenTone,
  decimalSign,
  toDecimalText,
} from '@entities/prc';
import { Notice } from '@widgets/FleetRegistry/ui';
import { formatHours, formatMoneyCell, formatNumber, formatRub, formatShare } from '../format';
import { NARROW, NumTable, StatGrid } from '../ui';

const LEVEL_CHIP: Readonly<Record<string, 'danger' | 'warning' | 'success'>> = {
  critical: 'danger',
  risk: 'warning',
  ok: 'success',
};

function levelLabel(level: string): string {
  return level === 'critical' || level === 'risk' || level === 'ok'
    ? BREAK_EVEN_LEVEL_LABEL[level]
    : level;
}

/**
 * Вывод блока 2 — текст ячейки H157 формы. Уровень и цветом, и словами: сам
 * текст сервера начинается с «КРИТИЧЕСКАЯ СИТУАЦИЯ», говорит «Риск высок» или
 * «Параметры приемлемы» — отдельная метка его бы повторила.
 */
export function BreakEvenVerdict({ data }: { data: PrcBreakEven }) {
  return (
    <Notice $tone={breakEvenTone(data.result.level)} role="status">
      <span className="text">{typo(data.result.message)}</span>
    </Notice>
  );
}

const Big = styled.span`
  display: block;
`;

/**
 * Показатели на телефоне — списком «подпись — значение»: длинные подписи
 * («Маржинальная прибыль MP = P − VC») и суммы в плитках по две не помещались.
 */
const KpiGrid = styled(StatGrid)`
  @container prc (max-width: ${NARROW}) {
    grid-template-columns: minmax(0, 1fr);

    & > div {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      column-gap: ${({ theme }) => theme.spacing(3)};
      align-items: baseline;
    }

    & dd {
      margin: 0;
      text-align: right;
      white-space: nowrap;
    }

    & dd.big {
      font-size: ${fleetType.lead};
    }
  }
`;

/** Показатели: MP, Q_BEP, R_BEP, запас прочности, прибыль при плане. */
export function BreakEvenKpis({ data }: { data: PrcBreakEven }) {
  const r = data.result;
  return (
    <KpiGrid $cols={5} aria-label="Показатели безубыточности">
      <div>
        <dt>Маржинальная прибыль MP = P − VC</dt>
        <dd className="big">
          <Big>{formatRub(r.mp)}</Big>
          <span className="sub">в час</span>
        </dd>
      </div>
      <div>
        <dt>Безубыточный объём Q_BEP</dt>
        <dd className="big">
          <Big>{r.q_bep === null ? 'нет' : formatHours(r.q_bep)}</Big>
          <span className="sub">в месяц</span>
        </dd>
      </div>
      <div>
        <dt>Точка безубыточности R_BEP</dt>
        <dd className="big">
          <Big>{r.r_bep === null ? 'нет' : formatRub(r.r_bep)}</Big>
          <span className="sub">выручка в месяц</span>
        </dd>
      </div>
      <div>
        <dt>Запас финансовой прочности</dt>
        <dd className="big">
          <Big>{r.margin === null ? '—' : formatShare(r.margin, 1)}</Big>
          <span className="sub">от выручки при плане</span>
        </dd>
      </div>
      <div>
        <dt>Результат при плане</dt>
        <dd className="big">
          <Big>{formatRub(r.profit_at_plan, true)}</Big>
          <span className="sub">в месяц</span>
        </dd>
      </div>
    </KpiGrid>
  );
}

const Need = styled.p`
  margin: ${({ theme }) => `${theme.spacing(3)} 0 0`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

/** Подсказка «на сколько поднять»: цена при плановой загрузке и загрузка при цене. */
export function BreakEvenNeed({ data }: { data: PrcBreakEven }) {
  const r = data.result;
  if (r.level === 'ok' || (r.required_price === null && r.required_hours === null)) return null;
  return (
    <Need>
      Чтобы выйти в ноль: цена часа не ниже <strong>{formatRub(r.required_price)}</strong> при
      плановой загрузке
      {r.required_hours !== null && (
        <>
          {' '}
          или загрузка не меньше <strong>{formatHours(r.required_hours)}</strong> в месяц при
          нынешней цене
        </>
      )}
      .
    </Need>
  );
}

const InputsTable = styled(Matrix)`
  width: auto;
  min-width: min(640px, 100%);

  /* Подпись таблицы — для чтения с экрана: над таблицей уже заголовок блока. */
  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  td {
    white-space: normal;
  }

  tbody th {
    min-width: 220px;
  }

  tr.sub > th {
    padding-left: ${({ theme }) => theme.spacing(6)};
    font-size: ${fleetType.small};
  }

  tr.sub > td {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Блок 1: исходные данные выбранного варианта. */
export function BreakEvenInputs({ data }: { data: PrcBreakEven }) {
  const i = data.inputs;
  const variant1 = data.variant === 1;
  return (
    <ScrollRegion label="Исходные данные безубыточности">
      <InputsTable>
        <caption>{typo(`Исходные данные, ${data.year} год, вариант ${data.variant}`)}</caption>
        <tbody>
          <tr>
            <th scope="row">P — цена часа без НДС</th>
            <td>{formatRub(i.price)}</td>
          </tr>
          <tr>
            <th scope="row">
              {variant1
                ? 'VC — переменные в час, с общецеховыми'
                : 'VC — переменные в час, без общецеховых'}
            </th>
            <td>{formatRub(i.vc)}</td>
          </tr>
          {i.vc_items.map((it) => (
            <tr key={it.article} className="sub">
              <th scope="row">{articleLabel(it.article)}</th>
              <td>{formatRub(it.value)}</td>
            </tr>
          ))}
          {variant1 && (
            <tr className="sub">
              <th scope="row">Общецеховые</th>
              <td>{formatRub(i.overhead)}</td>
            </tr>
          )}
          <tr>
            <th scope="row">
              {variant1
                ? 'FC — постоянные в месяц: амортизация'
                : 'FC — постоянные в месяц: амортизация + общецеховые × загрузка'}
            </th>
            <td>{formatRub(i.fc)}</td>
          </tr>
          {!variant1 && (
            <>
              <tr className="sub">
                <th scope="row">Амортизация в месяц</th>
                <td>{formatRub(i.fc_amortization)}</td>
              </tr>
              <tr className="sub">
                <th scope="row">Общецеховые × плановая загрузка</th>
                <td>{formatRub(i.overhead_month)}</td>
              </tr>
            </>
          )}
          <tr>
            <th scope="row">Q_план — плановая загрузка (годовой режим / 12)</th>
            <td>{formatHours(i.q_plan)} в месяц</td>
          </tr>
          <tr>
            <th scope="row">R_план — выручка при плане</th>
            <td>{formatRub(i.r_plan)}</td>
          </tr>
        </tbody>
      </InputsTable>
    </ScrollRegion>
  );
}

const SensTable = styled(NumTable)`
  td.c-title {
    min-width: 200px;
  }

  td.c-verdict {
    min-width: 260px;
    max-width: 420px;
    white-space: normal;
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
  }

  td.c-verdict > .msg {
    display: block;
    margin-top: ${({ theme }) => theme.spacing(1)};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  @media print {
    font-size: 8pt;
  }
`;

/** «Снижение цены на %» → «Снижение цены на 10 %». */
function scenarioTitle(title: string, change: string): string {
  const d = toDecimalText(change);
  if (!d || decimalSign(d) === 0) return title.replace(/ на %$/, '');
  return title.replace(/на %$/, `на ${formatShare(d.replace(/^-/, ''), 1)}`);
}

/** Блок 4: сценарии чувствительности. */
export function SensitivityTable({ data }: { data: PrcBreakEven }) {
  return (
    <ScrollRegion label="Чувствительность">
      <SensTable>
        <thead>
          <tr>
            <th scope="col">Сценарий</th>
            <th scope="col" className="num">
              P, ₽/ч
            </th>
            <th scope="col" className="num">
              VC, ₽/ч
            </th>
            <th scope="col" className="num">
              FC, ₽/мес
            </th>
            <th scope="col" className="num">
              MP, ₽/ч
            </th>
            <th scope="col" className="num">
              Q_BEP, ч
            </th>
            <th scope="col" className="num">
              R_BEP, ₽
            </th>
            <th scope="col" className="num">
              Запас
            </th>
            <th scope="col">Вывод</th>
          </tr>
        </thead>
        <tbody>
          {data.sensitivity.map((s) => (
            <tr key={s.scenario}>
              <td className={`c-title${s.scenario === 'base' ? ' strong' : ''}`}>
                {scenarioTitle(s.title, s.change)}
              </td>
              <td className="num">{formatMoneyCell(s.price)}</td>
              <td className="num">{formatMoneyCell(s.vc)}</td>
              <td className="num">{formatMoneyCell(s.fc)}</td>
              <td className="num">{formatMoneyCell(s.mp)}</td>
              <td className="num">{s.q_bep === null ? 'нет' : formatNumber(s.q_bep, 1)}</td>
              <td className="num">{s.r_bep === null ? 'нет' : formatMoneyCell(s.r_bep)}</td>
              <td className="num">{s.margin === null ? '—' : formatShare(s.margin, 1)}</td>
              <td className="c-verdict">
                <FleetChip $tone={LEVEL_CHIP[s.level] ?? 'neutral'}>
                  {levelLabel(s.level)}
                </FleetChip>
                <span className="msg">{typo(s.message)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </SensTable>
    </ScrollRegion>
  );
}
